/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
  ElementRef,
  untracked,
  AfterViewInit,
  OnDestroy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Cell,
  Column,
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  createAngularTable,
  FlexRenderDirective,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  isFunction,
  RowSelectionState,
  SortingState,
  Updater,
  VisibilityState
} from '@tanstack/angular-table';
import { DatatableResizableCellDirective } from '../datatable-resizable-cell/datatable-resizable-cell.directive';
import { DatatableResizableHeaderDirective } from '../datatable-resizable-header/datatable-resizable-header.directive';
import { DatatableEmptyComponent } from '../datatable-empty/datatable-empty.component';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import {
  CellClickEvent,
  ColumnHeaderClickEvent,
  DatatableConfig
} from '~core/models/datatable.models';
import { BaseComponent } from '~backoffice/app/~shared/abstract-components/base.component';
import { ResourceFacade } from '~backoffice/app/~store/facades/resource.facade';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';

const DEFAULT_CONFIG: DatatableConfig = {
  enableRowSelection: true,
  enableSorting: false,
  serverSideSorting: true,
  enableFiltering: true,
  enableColumnVisibility: true,
  enablePagination: true,
  enableColumnResizing: true,
  enableColumnHiding: true,
  enableColumnPinning: true,
  pageSize: 10,
  globalFilter: true,
  columnPinning: {
    left: [],
    right: ['actions']
  },
  maxHeight: '600px'
};

@Component({
  selector: 'datatable',
  templateUrl: './datatable.component.html',
  styleUrl: './datatable.component.scss',
  imports: [
    CommonModule,
    FlexRenderDirective,
    DatatableResizableHeaderDirective,
    DatatableResizableCellDirective,
    DatatableEmptyComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatCheckboxModule,
    MatTooltipModule,
    FormsModule,
    ReactiveFormsModule,
    MatPaginatorModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatatableComponent<T = unknown>
  extends BaseComponent
  implements AfterViewInit, OnDestroy
{
  private _resourceFacade = inject(ResourceFacade);

  // Inputs
  readonly data = input<T[]>([]);
  readonly columns = input<ColumnDef<T>[]>([]);
  readonly config = input<DatatableConfig>(DEFAULT_CONFIG);

  // Outputs
  readonly rowClick = output<T>();
  readonly rowDoubleClick = output<T>();
  readonly cellClick = output<CellClickEvent<T>>();
  readonly cellDoubleClick = output<CellClickEvent<T>>();
  readonly columnHeaderClick = output<ColumnHeaderClickEvent>();
  readonly columnHeaderDoubleClick = output<ColumnHeaderClickEvent>();
  readonly deleteSelected = output<string[]>();
  readonly sortingChange = output<SortingState>();

  protected readonly _mergedConfig = computed(() => ({
    ...DEFAULT_CONFIG,
    ...this.config()
  }));

  // Internal state
  protected readonly _columnOrder = signal<ColumnOrderState>([]);
  protected readonly _rowSelection = signal<RowSelectionState>({});
  protected readonly _columnVisibility = signal<VisibilityState>({});
  protected readonly _columnFilters = signal<ColumnFiltersState>([]);
  protected readonly _sorting = signal<SortingState>([]);
  protected readonly _globalFilter = signal('');
  protected readonly _pagination = signal({
    pageIndex: 0,
    pageSize: 1000_000 // this._mergedConfig().pageSize || 10 for client-side pagination
  });

  protected readonly _columnSizingInfo = computed(
    () => this.table.getState().columnSizingInfo
  );

  protected readonly _columnSizing = computed(
    () => this.table.getState().columnSizing
  );

  readonly table = createAngularTable<T>(() => ({
    data: this.data(),
    columns: this.columns(),
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: this._mergedConfig().enableRowSelection ?? true,
    getPaginationRowModel: this._mergedConfig().enablePagination
      ? getPaginationRowModel()
      : undefined,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel:
      this._mergedConfig().enableFiltering || this._mergedConfig().globalFilter
        ? getFilteredRowModel()
        : undefined,
    manualSorting: this._mergedConfig().serverSideSorting ?? false,
    onSortingChange: updaterOrValue => {
      this.toggleSorting(updaterOrValue);

      if (this._mergedConfig().serverSideSorting) {
        const newSorting = isFunction(updaterOrValue)
          ? updaterOrValue(this._sorting())
          : updaterOrValue;
        this.sortingChange.emit(newSorting);
      }
    },
    onColumnFiltersChange: updaterOrValue =>
      this.toggleColumnFilters(updaterOrValue),
    onColumnVisibilityChange: updaterOrValue =>
      this.toggleColumnVisibility(updaterOrValue),
    onRowSelectionChange: updaterOrValue =>
      this.toggleRowSelection(updaterOrValue),
    onPaginationChange: updaterOrValue => this.togglePagination(updaterOrValue),
    onColumnOrderChange: updaterOrValue =>
      this.toggleColumnOrder(updaterOrValue),
    globalFilterFn: 'includesString',
    enableColumnResizing: this._mergedConfig().enableColumnResizing,
    enableColumnHiding: this._mergedConfig().enableColumnHiding,
    state: {
      rowSelection: this._rowSelection(),
      columnVisibility: this._columnVisibility(),
      columnFilters: this._columnFilters(),
      columnOrder: this._columnOrder(),
      sorting: this._sorting(),
      pagination: this._pagination(),
      globalFilter: this._globalFilter(),
      columnPinning: this._mergedConfig().columnPinning ?? {
        left: [],
        right: ['actions']
      }
    },
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 40,
      maxSize: 800,
      enableSorting:
        (this._mergedConfig().enableSorting ||
          this._mergedConfig().serverSideSorting) ??
        false
    }
  }));

  protected readonly _columnSizeVars = computed(() => {
    void this._columnSizing();
    void this._columnSizingInfo();

    const headers = untracked(() => this.table.getFlatHeaders());
    const colSizes: { [key: string]: number } = {};

    let i = headers.length;

    while (--i >= 0) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  });

  protected readonly _visibleColumns = computed(() => this.getVisibleColumns());
  protected readonly _visibleColumnsIds = computed(() =>
    this._visibleColumns().map(column => column.id)
  );

  protected readonly _totalItems = computed(() => this.data().length);

  tableContainer = viewChild<ElementRef<HTMLTableElement>>('tableContainer');

  hasTableContainerOverflow = signal(false);
  tableContainerResizeObserver: ResizeObserver | undefined;

  ngAfterViewInit(): void {
    if (this._isPlatformBrowser) {
      this.listenToTableContainerOverflow();
      this._resourceFacade.paginationData$
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          tap(paginationData => {
            this._applySortingFromOrderBy(paginationData?.orderBy);
          })
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.tableContainerResizeObserver?.disconnect();
  }

  listenToTableContainerOverflow(): void {
    const tableContainer = this.tableContainer()?.nativeElement;
    if (!tableContainer) return;
    this.tableContainerResizeObserver = new ResizeObserver(elements => {
      const element = elements[0];
      const target = element.target as HTMLDivElement;
      this.hasTableContainerOverflow.set(
        target.scrollWidth > target.clientWidth
      );
    });

    this.tableContainerResizeObserver.observe(tableContainer);
  }

  // Toggle methods
  toggleRowSelection(updaterOrValue: Updater<RowSelectionState>): void {
    if (isFunction(updaterOrValue)) {
      this._rowSelection.update(prev => updaterOrValue(prev));
    } else {
      this._rowSelection.set(updaterOrValue);
    }
  }

  toggleColumnVisibility(updater: Updater<VisibilityState>): void {
    if (isFunction(updater)) {
      this._columnVisibility.update(updater);
    } else {
      this._columnVisibility.set(updater);
    }
  }

  toggleColumnFilters(updater: Updater<ColumnFiltersState>): void {
    if (isFunction(updater)) {
      this._columnFilters.update(updater);
    } else {
      this._columnFilters.set(updater);
    }
  }

  toggleSorting(updater: Updater<SortingState>): void {
    const newSorting = isFunction(updater) ? updater(this._sorting()) : updater;

    if (isFunction(updater)) {
      this._sorting.update(updater);
    } else {
      this._sorting.set(updater);
    }

    // Emit sorting changes for server-side sorting
    if (this._mergedConfig().serverSideSorting) {
      this.sortingChange.emit(newSorting);
    }
  }

  togglePagination(
    updater: Updater<{ pageIndex: number; pageSize: number }>
  ): void {
    if (isFunction(updater)) {
      this._pagination.update(updater);
    } else {
      this._pagination.set(updater);
    }
  }

  toggleColumnOrder(updater: Updater<ColumnOrderState>): void {
    if (isFunction(updater)) {
      this._columnOrder.update(updater);
    } else {
      this._columnOrder.set(updater);
    }
  }

  // Utility methods (public API for child components)
  getSelectedRows(): RowSelectionState {
    const _rowSelection = this._rowSelection();

    return _rowSelection;
  }

  getSelectedCount(): number {
    return Object.keys(this.getSelectedRows()).length;
  }

  setGlobalFilter(value: string): void {
    this._globalFilter.set(value);
  }

  onPreviousPage(): void {
    this.table.previousPage();
  }

  onNextPage(): void {
    this.table.nextPage();
  }

  canPreviousPage(): boolean {
    return this.table.getCanPreviousPage();
  }

  canNextPage(): boolean {
    return this.table.getCanNextPage();
  }

  getPageCount(): number {
    return this.table.getPageCount();
  }

  getPageIndex(): number {
    return this.table.getState().pagination.pageIndex;
  }

  getPageSize(): number {
    return this.table.getState().pagination.pageSize;
  }

  setPageSize(size: number): void {
    this.table.setPageSize(size);
  }

  /**
   * Handles pagination changes from mat-paginator
   * @param event The PageEvent from mat-paginator
   */
  onPaginationChange(event: PageEvent): void {
    this.togglePagination({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize
    });
  }

  /**
   * Gets the total number of items for mat-paginator length
   * @returns Total number of items in the data array
   */
  getTotalItems(): number {
    return this._totalItems();
  }

  getIsAllColumnsVisible(): boolean {
    return this.table.getIsAllColumnsVisible();
  }

  /**
   * Returns all leaf-node columns in the table flattened to a single level. This does not include parent columns.
   * @returns All leaf-node columns in the table.
   */
  getAllLeafColumns(): Column<T>[] {
    return this.table.getAllLeafColumns();
  }

  getVisibleColumns(): Column<T>[] {
    return this.table
      .getAllLeafColumns()
      .filter(column => column.getIsVisible());
  }

  toggleAllColumnsVisibility(event: Event): void {
    this.table.getToggleAllColumnsVisibilityHandler()(event);
  }

  onColumnVisibilityChange(selectedColumnIds: string[]): void {
    const allColumns = this.table.getAllLeafColumns();
    const visibilityState: VisibilityState = {};

    allColumns.forEach(column => {
      // Skip actions column from visibility control
      if (column.id === 'actions') {
        visibilityState[column.id] = true; // Always keep actions column visible
      } else {
        visibilityState[column.id] = selectedColumnIds.includes(column.id);
      }
    });

    this.toggleColumnVisibility(visibilityState);
  }

  /**
   * Checks if a column is visible
   * @param columnId The ID of the column to check
   * @returns true if the column is visible, false otherwise
   */
  isColumnVisible(columnId: string): boolean {
    const column = this.table
      .getAllLeafColumns()
      .find(col => col.id === columnId);
    if (!column) return false;
    return column.getIsVisible();
  }

  /**
   * Checks if all non-protected columns are visible
   * @returns true if all columns are visible, false otherwise
   */
  areAllColumnsVisible(): boolean {
    const allColumns = this.table.getAllLeafColumns();
    const nonProtectedColumns = allColumns.filter(
      col => !['actions', 'select'].includes(col.id)
    );

    if (nonProtectedColumns.length === 0) return true;

    return nonProtectedColumns.every(column => column.getIsVisible());
  }

  /**
   * Toggles visibility of all non-protected columns (Show/Hide All)
   */
  toggleAllColumnsVisibilityState(): void {
    if (!this._mergedConfig().enableColumnHiding) {
      return;
    }

    const allColumns = this.table.getAllLeafColumns();
    const allVisible = this.areAllColumnsVisible();
    const newVisibility: VisibilityState = {};

    allColumns.forEach(column => {
      // Keep protected columns always visible
      if (['actions', 'select'].includes(column.id)) {
        newVisibility[column.id] = true;
      } else {
        // Toggle all other columns
        newVisibility[column.id] = !allVisible;
      }
    });

    this.toggleColumnVisibility(newVisibility);
  }

  /**
   * Toggles the visibility of a single column
   * @param columnId The ID of the column to toggle
   */
  toggleColumnVisibilityById(columnId: string): void {
    if (['actions', 'select'].includes(columnId)) {
      return;
    }

    if (!this._mergedConfig().enableColumnHiding) {
      return;
    }

    const allColumns = this.table.getAllLeafColumns();
    const currentVisibility = this._columnVisibility();
    const isCurrentlyVisible = this.isColumnVisible(columnId);

    // Build new visibility state preserving all columns
    const newVisibility: VisibilityState = {};

    allColumns.forEach(column => {
      if (['actions', 'select'].includes(column.id)) {
        newVisibility[column.id] = true;
      } else if (column.id === columnId) {
        // Toggle the target column
        newVisibility[column.id] = !isCurrentlyVisible;
      } else {
        // Preserve existing visibility state, default to true if not set
        newVisibility[column.id] = currentVisibility[column.id] ?? true;
      }
    });

    this.toggleColumnVisibility(newVisibility);
  }

  handleRowClick(rowData: T): void {
    this.rowClick.emit(rowData);
  }

  handleRowDoubleClick(rowData: T): void {
    this.rowDoubleClick.emit(rowData);
  }

  handleCellClick(cell: Cell<T, unknown>, event: Event): void {
    // Prevent click actions on action columns
    if (cell.column.id === 'actions') {
      event.stopPropagation();
      return;
    }
    const cellEvent: CellClickEvent<T> = {
      value: cell.getValue(),
      rowData: cell.row.original,
      columnId: cell.column.id,
      cell: {
        row: { original: cell.row.original },
        column: { id: cell.column.id }
      }
    };
    this.cellClick.emit(cellEvent);
  }

  handleCellDoubleClick(cell: Cell<T, unknown>, event: Event): void {
    // Prevent double-click actions on action columns
    if (cell.column.id === 'actions') {
      event.stopPropagation();
      return;
    }
    const cellEvent: CellClickEvent<T> = {
      value: cell.getValue(),
      rowData: cell.row.original,
      columnId: cell.column.id,
      cell: {
        row: { original: cell.row.original },
        column: { id: cell.column.id }
      }
    };
    this.cellDoubleClick.emit(cellEvent);
  }

  handleColumnHeaderClick(header: Header<T, unknown>, _event: Event): void {
    const headerEvent: ColumnHeaderClickEvent = {
      columnId: header.column.id,
      column: {
        id: header.column.id,
        canSort: header.column.getCanSort(),
        isSorted: header.column.getIsSorted()
      }
    };
    this.columnHeaderClick.emit(headerEvent);
  }

  handleColumnHeaderDoubleClick(
    header: Header<T, unknown>,
    _event: Event
  ): void {
    const headerEvent: ColumnHeaderClickEvent = {
      columnId: header.column.id,
      column: {
        id: header.column.id,
        canSort: header.column.getCanSort(),
        isSorted: header.column.getIsSorted()
      }
    };
    this.columnHeaderDoubleClick.emit(headerEvent);
  }

  getSelectedObjects(): T[] {
    return this.table.getSelectedRowModel().rows.map(row => row.original);
  }

  getSelectedIds(): string[] {
    return this.getSelectedObjects()
      .map(row => (row as { id?: string }).id)
      .filter((id): id is string => !!id);
  }

  /**
   * Gets the current sorting state
   * @returns The current sorting state
   */
  getSortingState(): SortingState {
    return this._sorting();
  }

  /**
   * Sets the sorting state (useful for initializing from server)
   * @param sorting The sorting state to set
   */
  setSortingState(sorting: SortingState): void {
    this._sorting.set(sorting);
  }

  /**
   * Clears sorting for a specific column
   * @param header The header of the column to clear sorting for
   */
  clearSorting(header: Header<T, unknown>): void {
    const columnId = header.column.id;

    // Check if sorting is enabled
    if (!this._mergedConfig().enableSorting) {
      return;
    }

    // Check if column can be sorted
    if (!header.column.getCanSort()) {
      return;
    }

    // Check if column is currently sorted
    if (!header.column.getIsSorted()) {
      return;
    }

    // Remove this column from the sorting state
    this.toggleSorting(prev => prev.filter(sort => sort.id !== columnId));
  }

  /**
   * Sorts a column in ascending order
   * @param header The header of the column to sort
   */
  sortAscending(header: Header<T, unknown>): void {
    const columnId = header.column.id;

    if (!this._mergedConfig().enableSorting) {
      return;
    }

    if (!header.column.getCanSort()) {
      return;
    }

    this.toggleSorting(prev => {
      const filtered = prev.filter(sort => sort.id !== columnId);
      return [...filtered, { id: columnId, desc: false }];
    });
  }

  /**
   * Sorts a column in descending order
   * @param header The header of the column to sort
   */
  sortDescending(header: Header<T, unknown>): void {
    const columnId = header.column.id;

    if (!this._mergedConfig().enableSorting) {
      return;
    }

    if (!header.column.getCanSort()) {
      return;
    }

    this.toggleSorting(prev => {
      const filtered = prev.filter(sort => sort.id !== columnId);
      return [...filtered, { id: columnId, desc: true }];
    });
  }

  /**
   * Hides a specific column by its header
   * @param header The header of the column to hide
   */
  hideColumn(header: Header<T, unknown>): void {
    const columnId = header.column.id;

    if (['actions', 'select'].includes(columnId)) {
      return;
    }

    if (!this._mergedConfig().enableColumnHiding) {
      return;
    }

    if (header.column.getCanHide() === false) {
      return;
    }

    const allColumns = this.table.getAllLeafColumns();
    const currentVisibility = this._columnVisibility();

    const newVisibility: VisibilityState = {};

    allColumns.forEach(column => {
      if (['actions', 'select'].includes(column.id)) {
        newVisibility[column.id] = true;
      } else if (column.id === columnId) {
        newVisibility[column.id] = false;
      } else {
        newVisibility[column.id] = currentVisibility[column.id] ?? true;
      }
    });

    this.toggleColumnVisibility(newVisibility);
  }

  readonly getCommonPinningStyles = (
    column: Column<T>,
    isHeader = false
  ): Record<string, any> => {
    const isPinned = this.hasTableContainerOverflow() && column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left');
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right');

    let background = '';
    if (isPinned) {
      background = isHeader ? '#f5f5f5 ' : 'white';
    }

    return {
      boxShadow: isLastLeftPinnedColumn
        ? '-2px 0 2px -2px gray inset'
        : isFirstRightPinnedColumn
          ? '2px 0 2px -2px gray inset'
          : undefined,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      opacity: isPinned ? 0.95 : 1,
      position: isPinned ? 'sticky' : 'relative',
      background,
      width: `${column.getSize()}px`,
      zIndex: isPinned ? 1 : 0
    };
  };

  /**
   * Checks if an element is truncated/clamped
   * @param el The HTML element to check
   * @returns true if the element is truncated, false otherwise
   */
  protected _isTruncated = (el: HTMLElement | null): boolean => {
    if (!el) {
      return false;
    }
    return el.scrollWidth > el.clientWidth;
  };

  /**
   * Gets the text content of a cell for tooltip display
   * @param cellContent The cell content
   * @returns The text content as a string
   */
  getCellTooltipText(cellContent: unknown): string {
    if (cellContent === null || cellContent === undefined) {
      return '';
    }
    return String(cellContent);
  }

  protected _shouldShowSortIndicator(header: Header<T, unknown>): boolean {
    // Don't show for actions column
    if (header.column.id === 'actions') {
      return false;
    }

    return header.column.getCanSort() === true;
  }

  private _applySortingFromOrderBy(orderBy?: Record<string, unknown>): void {
    const orderByEntries = Object.entries(orderBy || {}) ?? [];
    this.setSortingState(
      orderByEntries.map(([columnId, direction]) => {
        const directionValue = direction
          ? (direction as string).toLocaleLowerCase()
          : 'desc';
        return {
          id: columnId,
          desc: directionValue === 'desc'
        };
      })
    );
  }
}
